import React from "react";
import { useTranslation } from "react-i18next";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import CancelIcon from "@mui/icons-material/Cancel";
import PropTypes from "prop-types";
import { CollapseCard, Button, DateTime, IconButton, Autocomplete } from "@totalsoft/rocket-ui";
import { Grid, Table, TableBody, TableHead, TableRow, TableCell } from "@mui/material";
import CardNoResultsWrapper from "components/layout/CardNoResultsWrapper";
import { DenseHeaderCell } from "components/common/TableStyles";
import { isValid, getErrors } from "@totalsoft/pure-validations-react";
import { useReactiveVar } from "@apollo/client";
import { synchronizationInProgressVar } from "variables";

const PersonWorkplacesComponent = ({
  personWorkplaces,
  professionData,
  positions,
  employers,
  hasWritePermission,
  saveDisabled,
  onAdd,
  onModify,
  onSave,
  onDelete,
  validationPersonWorkplaces
}) => {
  const { t } = useTranslation();
  const synchronizationInProgress = useReactiveVar(synchronizationInProgressVar);

  return (
    <CollapseCard
      id="personWorkplacesCard"
      icon={FilePresentIcon}
      iconColor="primary"
      title={t("Partner.PersonWorkplaces")}
      defaultExpanded={synchronizationInProgress}
      toggleOnHeaderClick
    >
      <Grid container>
        <Grid item xs={12} sm={12} md={12} sx={{ overflowX: "auto" }}>
          <CardNoResultsWrapper content={personWorkplaces}>
            <Table>
              <TableHead>
                <TableRow>
                  <DenseHeaderCell>{t("PersonWorkplaces.Profession")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("PersonWorkplaces.Function")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("PersonWorkplaces.EmployerName")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("PersonWorkplaces.DateFrom")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("PersonWorkplaces.DateTo")}</DenseHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {personWorkplaces?.map((item, idx) => {
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <Autocomplete
                          simpleValue={true}
                          value={item.professionId}
                          onChange={event => {
                            onModify(event, "professionId", idx);
                          }}
                          disabled={!hasWritePermission}
                          isClearable={false}
                          valueKey="professionId"
                          labelKey="professionName"
                          options={professionData}
                          error={!isValid(validationPersonWorkplaces[idx].professionId)}
                          helperText={getErrors(validationPersonWorkplaces[idx].professionId)}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          simpleValue={true}
                          value={item.positionId}
                          onChange={event => {
                            onModify(event, "positionId", idx);
                          }}
                          disabled={!hasWritePermission}
                          isClearable={false}
                          valueKey="positionId"
                          labelKey="positionName"
                          options={positions}
                          error={!isValid(validationPersonWorkplaces[idx].positionId)}
                          helperText={getErrors(validationPersonWorkplaces[idx].positionId)}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          simpleValue={true}
                          value={item.employerId}
                          onChange={event => {
                            onModify(event, "employerId", idx);
                          }}
                          disabled={!hasWritePermission}
                          isClearable={false}
                          valueKey="employerId"
                          labelKey="employerName"
                          options={employers}
                          error={!isValid(validationPersonWorkplaces[idx].employerId)}
                          helperText={getErrors(validationPersonWorkplaces[idx].employerId)}
                        />
                      </TableCell>
                      <TableCell>
                        <DateTime
                          value={item.dateFrom}
                          onChange={event => {
                            onModify(event, "dateFrom", idx);
                          }}
                          disabled={!hasWritePermission}
                          error={!isValid(validationPersonWorkplaces[idx].dateFrom)}
                          helperText={
                            getErrors(validationPersonWorkplaces[idx].dateFrom) !==
                              t(`Validations.Generic.Mandatory`) && getErrors(validationPersonWorkplaces[idx].dateFrom)
                              ? t(
                                  `PersonWorkplaces.${getErrors(validationPersonWorkplaces[idx].dateFrom).substring(
                                    0,
                                    27
                                  )}`
                                ) +
                                getErrors(validationPersonWorkplaces[idx].dateFrom).substring(
                                  27,
                                  getErrors(validationPersonWorkplaces[idx].dateFrom).length
                                )
                              : getErrors(validationPersonWorkplaces[idx].dateFrom)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <DateTime
                          value={item.dateTo}
                          onChange={event => {
                            onModify(event, "dateTo", idx);
                          }}
                          disabled={!hasWritePermission}
                          error={!isValid(validationPersonWorkplaces[idx].dateTo)}
                          helperText={
                            getErrors(validationPersonWorkplaces[idx].dateTo) !== t(`Validations.Generic.Mandatory`) &&
                            getErrors(validationPersonWorkplaces[idx].dateTo)
                              ? t(
                                  `PersonWorkplaces.${getErrors(validationPersonWorkplaces[idx].dateTo).substring(
                                    0,
                                    30
                                  )}`
                                ) +
                                getErrors(validationPersonWorkplaces[idx].dateTo).substring(
                                  30,
                                  getErrors(validationPersonWorkplaces[idx].dateTo).length
                                )
                              : getErrors(validationPersonWorkplaces[idx].dateTo)
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "end" }}>
                        <IconButton
                          id={"personWorkplaces_" + idx + "_deleteBtn"}
                          color="primary"
                          variant="text"
                          onClick={() => {
                            onDelete(idx);
                          }}
                          disabled={!hasWritePermission}
                        >
                          <CancelIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardNoResultsWrapper>
          <Button
            id="personWorkplaces_addBtn"
            color="primary"
            variant="contained"
            onClick={onAdd}
            disabled={!hasWritePermission}
            sx={{ marginTop: "1.5rem" }}
          >
            {t("Add")}
          </Button>
          <Button
            id="personWorkplaces_saveBtn"
            color="primary"
            variant="contained"
            onClick={onSave}
            disabled={!hasWritePermission || saveDisabled || !isValid(validationPersonWorkplaces)}
            sx={{ marginLeft: ".25rem", marginTop: "1.5rem" }}
          >
            {t("Save")}
          </Button>
        </Grid>
      </Grid>
    </CollapseCard>
  );
};
PersonWorkplacesComponent.propTypes = {
  personWorkplaces: PropTypes.array.isRequired,
  professionData: PropTypes.array.isRequired,
  positions: PropTypes.array.isRequired,
  employers: PropTypes.array.isRequired,
  hasWritePermission: PropTypes.bool.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
  onModify: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  validationPersonWorkplaces: PropTypes.object
};
export default PersonWorkplacesComponent;
